"use client";
import type {
  AuditImageCardProps,
  AuditImageSectionProps,
} from "../../../types/audit-image";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  AlertCircle,
  Camera,
  Eye,
  HardHat,
  Leaf,
  Loader2,
  Map,
  Navigation,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import { useStableMemoComponent } from "../../../hooks/use-stable-memo-component";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { OfflineImage } from "../../shared/offline-image";
import { Alert, AlertDescription } from "../../ui/alert.jsx";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedCheckbox,
  OptimizedCheckboxGroup,
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";

import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { sharedSiteProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { ImageCapturePicker } from "./image-capture-picker";
import { LocalSectionImportControl } from "./local-section-import-control";

interface SiteDataFormProps extends AuditFormProps {
  siteId: string;
  templateId: string;
  reportId: string;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  readOnly?: boolean;
  previousCompletedReports?: any[];
}

interface SiteImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface SiteData {
  // Topography
  topography: string;
  topographyNotes: string;

  // Access to Site
  accessToSite: string;
  visibilitySplays: string;
  narrowBridge: boolean;
  lowHeightBridge: boolean;
  accessRoadWidth: string;

  // Description (Multi-select)
  description: string[];

  // Ground / Rooftop Condition (Multi-select)
  groundRooftopCondition: string[];

  // Additional Information
  additionalObservations: string;
  siteImages: SiteImage[];
}

const initialData: SiteData = {
  // Topography
  topography: "",
  topographyNotes: "",

  // Access to Site
  accessToSite: "",
  visibilitySplays: "",
  narrowBridge: false,
  lowHeightBridge: false,
  accessRoadWidth: "",

  // Description
  description: [],

  // Ground / Rooftop Condition
  groundRooftopCondition: [],

  // Additional Information
  additionalObservations: "",
  siteImages: [],
};

const topographyOptions = [
  { value: "flat", label: "Flat" },
  { value: "slope_ns", label: "Slope N-S" },
  { value: "slope_ew", label: "Slope E-W" },
  // { value: "hilly", label: "Hilly" },
  // { value: "uneven", label: "Uneven" },
  { value: "mixed", label: "Mixed Terrain" },
  { value: "rooftop", label: "Rooftop" },
  { value: "pitched", label: "Pitched" },
  { value: "barrel", label: "barrel" },
  { value: "dome", label: "Dome" },
];

// Access to Site Options
const accessTypeOptions = [
  { value: "road_access", label: "Road Access" },
  { value: "track_access", label: "Track/Unpaved Road" },
  { value: "field_access", label: "Field Access" },
  { value: "restricted_access", label: "Restricted Access" },
  { value: "pedestrian_only", label: "Pedestrian Only" },
  {
    value: "vehicle_full_visibility",
    label: "Vehicle Access with Full Visibility",
  },
  {
    value: "vehicle_limited_visibility",
    label: "Vehicle Access with Limited Visibility",
  },
  {
    value: "vehicle_with_marshal",
    label: "Vehicle Access with Traffic Marshal",
  },
];

const siteConditionOptions = [
  {
    value: "agricultural_land_unharvested",
    label: "Land currently used agriculturally and not yet harvested",
  },
  {
    value: "land_already_harvested",
    label: "The land is already harvested",
  },
  {
    value: "area_grazed",
    label: "Area is grazed",
  },
  {
    value: "fenced_area",
    label: "Area is fenced in and must be unlocked for the time of the survey",
  },
  {
    value: "trees_or_shrubs_present",
    label: "There are trees and/or shrubs on the land",
  },
  {
    value: "earth_moving_required",
    label: "Earth moving still must be done at the site",
  },
  {
    value: "earth_moving_ongoing",
    label: "Earth moving is presently being done at the site",
  },
  {
    value: "earth_moving_planned",
    label: "Earth moving is planned to be carried out at the site",
  },
  {
    value: "bridge_drainage_watercourse_present",
    label: "Bridge / drainage / water course present",
  },
  {
    value: "vegetation_to_maintain",
    label: "Hedge rows/trees to be maintained",
  },
  {
    value: "soft_ground_conditions",
    label:
      "The ground at the site is currently soft and muddy due to rain / ground water",
  },
  {
    value: "shadow_objects_present",
    label:
      "Trees, (future) buildings, mountains or other objects around that may cause shadow present",
  },
  {
    value: "fra_land_management_plan_available",
    label: "FRA / land management plan available",
  },
  {
    value: "aggressive_or_salty_atmosphere",
    label:
      "Sources of aggressive (industry) or salty (sea) atmosphere / distance from the sea",
  },
  {
    value: "flat_roof",
    label: "Flat roof",
  },
  {
    value: "pitched_roof",
    label: "Pitched roof",
  },
  {
    value: "roof_obstructions_present",
    label: "Roof obstructions present (HVAC units, vents, skylights, etc.)",
  },
  {
    value: "roof_access_available",
    label: "Safe roof access available",
  },
  {
    value: "roof_condition_good",
    label: "Roof is in good structural condition",
  },
  {
    value: "roof_shading_present",
    label: "Nearby buildings or rooftop structures causing shading",
  },
].sort((a, b) => a.label.localeCompare(b.label));

// Ground / Rooftop Condition Options (applies to both ground-mount and rooftop sites)
const groundRooftopConditionOptions = [
  { value: "wet", label: "Wet" },
  { value: "slippery", label: "Slippery" },
  { value: "trip_hazards", label: "Trip Hazards" },
  { value: "uneven_surface", label: "Uneven Surface" },
  { value: "loose_debris", label: "Loose Debris / Materials" },
  { value: "standing_water", label: "Standing Water / Ponding" },
  { value: "icy_frost", label: "Icy / Frost" },
  { value: "unstable_soft_ground", label: "Unstable / Soft Ground" },
  { value: "restricted_confined_space", label: "Restricted / Confined Space" },
  { value: "poor_lighting", label: "Poor Lighting" },
  { value: "edge_fall_hazard", label: "Edge / Fall Hazard" },
  { value: "no_hazards_identified", label: "No Hazards Identified" },
];

export const SiteDataForm = memo(function SiteDataForm({
  siteId,
  templateId,
  reportId,
  setSessionProgress,
  sessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: SiteDataFormProps) {
  const { notify } = useNotification();

  const [formData, setFormData] = useState<SiteData>(initialData);
  const formDataRef = useLatest(formData);
  const [selectedImage, setSelectedImage] = useState<SiteImage | null>(null);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: boolean;
  }>({});

  const { loading } = useAppSelector((state) => state?.files);
  const currentSiteData = useVersionAwareSiteData(false);
  const dispatch = useAppDispatch();
  const activeReportId =
    typeof window !== "undefined"
      ? window.sessionStorage?.getItem("activeReportId") ||
        window.localStorage?.getItem("activeReportId") ||
        ""
      : "";

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power). Mirrors the pattern in site-overview.tsx.
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:site-data` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    dispatch(fetchFiles({ siteId, reportId }) as any);
  }, [siteId, reportId, dispatch]);

  console.log(
    "SiteDataForm: siteId, reportId, templateId",
    siteId,
    reportId,
    templateId,
  );

  // Load version data when viewing historical version
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below. Only applies if a draft exists, so a freshly-fetched
  // server record from a different session is never clobbered unexpectedly.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev: any) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once the local form state matches what's confirmed saved on the server,
  // the IndexedDB draft is redundant — clear it so a future mount doesn't
  // restore stale local data over a fresher server record.
  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    const prog = sharedSiteProgress(formData);
    setSessionProgress((prev) => ({
      ...prev,
      [templateId]: { ...prev[templateId], "site-data": prog },
    }));
  }, [formData, setSessionProgress, templateId]);

  const handleSave = useCallback(
    async (data: SiteData) => {
      try {
        if (!siteId || !activeReportId) {
          throw new Error("Site or report context is missing");
        }

        // await dispatch(
        //   updateSiteOverviewData({
        //     siteId,
        //     reportId: activeReportId,
        //     payload: {
        //       environmentalData: {
        //         siteTopography: {
        //           topography: data.topography,
        //           notes: data.topographyNotes,
        //         },
        //       },
        //       accessibilityInfo: {
        //         accessToSite: data.accessToSite,
        //         visibilitySplays: data.visibilitySplays,
        //         narrowBridge: data.narrowBridge,
        //         lowHeightBridge: data.lowHeightBridge,
        //         accessRoadWidth: data.accessRoadWidth,
        //       },
        //       description: data.description,
        //       additionalObservations: data.additionalObservations,
        //     },
        //   }),
        // );

        const mutationResult1 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: {
                environmentalData: {
                  siteTopography: {
                    topography: data.topography,
                    notes: data.topographyNotes,
                  },
                },
                accessibilityInfo: {
                  accessToSite: data.accessToSite,
                  visibilitySplays: data.visibilitySplays,
                  narrowBridge: data.narrowBridge,
                  lowHeightBridge: data.lowHeightBridge,
                  accessRoadWidth: data.accessRoadWidth,
                },
                description: data.description,
                groundRooftopCondition: data.groundRooftopCondition,
                additionalObservations: data.additionalObservations,
              },
            }),
          ),
        );

        const config = notificationHelpers.formSaveSuccess("Site Data", [
          mutationResult1,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Site Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [siteId, activeReportId, dispatch, notify],
  );

  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7000,
    onSave: handleSave,
    onError: (error) => console.error("Save error:", error),
    readOnly,
  });

  useEffect(() => {
    if (readOnly && currentSiteData) {
      const newFormData = {
        ...formDataRef.current,
        // Topography
        topography:
          currentSiteData?.environmentalData?.siteTopography?.topography || "",
        topographyNotes:
          currentSiteData?.environmentalData?.siteTopography?.notes || "",

        // Access to Site
        accessToSite: currentSiteData?.accessibilityInfo?.accessToSite || "",
        visibilitySplays:
          currentSiteData?.accessibilityInfo?.visibilitySplays || "",
        narrowBridge: currentSiteData?.accessibilityInfo?.narrowBridge || false,
        lowHeightBridge:
          currentSiteData?.accessibilityInfo?.lowHeightBridge || false,
        accessRoadWidth:
          currentSiteData?.accessibilityInfo?.accessRoadWidth || "",

        // Description
        description: currentSiteData?.description || [],

        // Ground / Rooftop Condition
        groundRooftopCondition: currentSiteData?.groundRooftopCondition || [],

        // Additional Information
        additionalObservations: currentSiteData?.additionalObservations || "",
      };
      resetFormBaseline(newFormData);
      setFormData(newFormData);
      return;
    }

    if (!siteId || !activeReportId || readOnly) return;

    const loadReportSiteInformation = async () => {
      try {
        const newFormData = {
          ...initialData,
          topography:
            currentSiteData?.environmentalData?.siteTopography?.topography ||
            "",
          topographyNotes:
            currentSiteData?.environmentalData?.siteTopography?.notes || "",
          accessToSite: currentSiteData?.accessibilityInfo?.accessToSite || "",
          visibilitySplays:
            currentSiteData?.accessibilityInfo?.visibilitySplays || "",
          narrowBridge:
            currentSiteData?.accessibilityInfo?.narrowBridge || false,
          lowHeightBridge:
            currentSiteData?.accessibilityInfo?.lowHeightBridge || false,
          accessRoadWidth:
            currentSiteData?.accessibilityInfo?.accessRoadWidth || "",
          description: currentSiteData?.description || [],
          groundRooftopCondition: currentSiteData?.groundRooftopCondition || [],
          additionalObservations: currentSiteData?.additionalObservations || "",
        };

        resetFormBaseline(newFormData);
        setFormData(newFormData);
      } catch (error) {
        const fallbackFormData = {
          ...initialData,
          topography:
            currentSiteData?.environmentalData?.siteTopography?.topography ||
            "",
          topographyNotes:
            currentSiteData?.environmentalData?.siteTopography?.notes || "",
          accessToSite: currentSiteData?.accessibilityInfo?.accessToSite || "",
          visibilitySplays:
            currentSiteData?.accessibilityInfo?.visibilitySplays || "",
          narrowBridge:
            currentSiteData?.accessibilityInfo?.narrowBridge || false,
          lowHeightBridge:
            currentSiteData?.accessibilityInfo?.lowHeightBridge || false,
          accessRoadWidth:
            currentSiteData?.accessibilityInfo?.accessRoadWidth || "",
          description: currentSiteData?.description || [],
          groundRooftopCondition: currentSiteData?.groundRooftopCondition || [],
          additionalObservations: currentSiteData?.additionalObservations || "",
        };
        resetFormBaseline(fallbackFormData);
        setFormData(fallbackFormData);
      }
    };

    loadReportSiteInformation();
  }, [
    siteId,
    activeReportId,
    currentSiteData,
    readOnly,
    resetFormBaseline,
    formDataRef,
  ]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult2 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for image descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
  }, [forceFormSave, forceImageSave]);

  const handleInputChange = useCallback(
    (
      field: keyof SiteData,
      value: string | boolean | string[] | SiteImage[],
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleImageUpload = useCallback(
    (files: FileList | File[] | null, filterData) => {
      if (!files) return;
      const fileArray = Array.from(files);

      const uploadKey = JSON.stringify(filterData);
      setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));

      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId,
              entityType: "site-data",
              entityId: reportId,
              reportId,
            },
            filterData,
          }),
        ),
      )
        .then((mutationOutcome) => {
          // Show success notification for each uploaded file
          fileArray.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          // Show error notification
          const config = notificationHelpers.imageUploadError(
            fileArray.length === 1
              ? fileArray[0].name
              : `${fileArray.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
        });
    },
    [notify, reportId, siteId, dispatch],
  );

  const updateImageDescription = useCallback(
    (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    },
    [setImageDescriptions],
  );

  const handleRemoveImage = useCallback((imageId: string) => {
    setImageToDelete(imageId);
  }, []);

  const confirmRemoveImage = useCallback(() => {
    if (imageToDelete) {
      requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "Image",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "Image",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setImageToDelete(null);
        });
    }
  }, [imageToDelete, dispatch, notify]);

  const cancelRemoveImage = useCallback(() => {
    setImageToDelete(null);
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  const isFormValid = useMemo(() => {
    return (
      formData.topography.trim() !== "" && formData.accessToSite.trim() !== ""
    );
  }, [formData]);

  // Helper function to check if any upload is in progress for a specific section
  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingImages[uploadKey] || false;
  };

  // Image Card Component
  const ImageCard = useStableMemoComponent<AuditImageCardProps<SiteImage>>(
    ({
      image,
      onView,
      onRemove,
      onDescriptionUpdate,
      isDeleting,
      imageDescription,
    }) => (
      <div key={image.id} className="relative group">
        <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
          <OfflineImage
            fileId={image?.id}
            src={image?.fileUrl}
            alt="Site image"
            className="w-full h-full object-cover"
          />
          {loading && (isDeleting === true || isDeleting === image.id) && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
            onClick={() => onView(image)}
            disabled={loading}
            title="View Image"
          >
            <Eye className="w-3 h-3" />
          </Button>
          {!readOnly && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
              onClick={() => onRemove(image.id)}
              disabled={loading}
              title="Delete Image"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate truncate">
              {image?.originalName}
            </span>
            <Badge variant="outline" className="text-xs">
              {formatFileSize(image?.fileSize)}
            </Badge>
          </div>
          <textarea
            value={imageDescription}
            onChange={(e) => onDescriptionUpdate(image.id, e.target.value)}
            placeholder="Add description..."
            rows={2}
            className="w-full p-2 border rounded-md text-xs resize-none"
            disabled={loading}
            readOnly={readOnly}
          />
          <div className="text-xs text-slate-500 dark:text-muted-foreground">
            Date: {new Date(image.uploadDate).toLocaleDateString()}
          </div>
        </div>
      </div>
    ),
    (prevProps, nextProps) => {
      const propsEqual =
        prevProps.image.id === nextProps.image.id &&
        prevProps.imageDescription === nextProps.imageDescription &&
        prevProps.isDeleting === nextProps.isDeleting;
      return propsEqual;
    },
  );

  // Image Section Component
  const ImageSection = useStableMemoComponent<AuditImageSectionProps>(
    ({
      title,
      filterData,
      uploadFilterData,
      emptyMessage,
      buttonText = "Add Images",
    }) => {
      const sectionImages = files?.filter(
        (file) =>
          file?.relatedTo?.filterData &&
          JSON.stringify(file?.relatedTo?.filterData) ===
            JSON.stringify(filterData),
      );

      const isUploading = isUploadingForSection(uploadFilterData);

      return (
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{title}</h4>
            {!readOnly && (
              <ImageCapturePicker
                buttonText={buttonText}
                isUploading={isUploading}
                disabled={loading}
                onConfirm={(selectedFiles) =>
                  handleImageUpload(selectedFiles, uploadFilterData)
                }
              />
            )}
          </div>

          {isUploading && (
            <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
              <div className="text-center">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
                <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
              </div>
            </div>
          )}

          {!isUploading && sectionImages.length === 0 ? (
            <div className="flex items-center p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                    No images added
                  </p>
                  <p className="text-xs text-slate-500 dark:text-muted-foreground">{emptyMessage}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sectionImages.map((image) => (
                <ImageCard
                  key={image.id}
                  imageDescription={
                    imageDescriptions[image.id] ?? image?.fileDescription ?? ""
                  }
                  image={image}
                  onView={setSelectedImage}
                  onRemove={handleRemoveImage}
                  onDescriptionUpdate={updateImageDescription}
                  isDeleting={loading ? imageToDelete : false}
                />
              ))}
            </div>
          )}
        </div>
      );
    },
  );

  return (
    <div className="space-y-4 md:space-y-6">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="site-data"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
        />
      )}

      {/* Floating save section - hidden when viewing read-only version */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Topography of the Site */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Map className="w-5 h-5 text-blue-600" />
            <CardTitle>Site Topography</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <OptimizedSelect
            label="Topography Type"
            value={formData.topography}
            onChange={(value) => handleInputChange("topography", value)}
            placeholder="Select topography type"
            options={topographyOptions}
            readOnly={readOnly}
          />

          <ImageSection
            title="Topography Images"
            filterData={{ topography: true }}
            uploadFilterData={{ topography: true }}
            emptyMessage="Add images to document site topography"
            buttonText="Add Topography Images"
          />
          {/* <div className="space-y-2">
            <OptimizedTextarea
              id="topographyNotes"
              label="Topography Description"
              value={formData.topographyNotes}
              onChange={(value) => handleInputChange("topographyNotes", value)}
              readOnly={readOnly}> handleInputChange("topographyNotes", value)}
              placeholder="Describe any specific topographical features or challenges..."
              rows={3}
            />
          </div> */}
        </CardContent>
      </Card>

      {/* Access to the Site */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Navigation className="w-5 h-5 text-green-600" />
            <CardTitle>Access to the Site</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <OptimizedSelect
              label="Access Type"
              value={formData.accessToSite || ""}
              onChange={(value) => handleInputChange("accessToSite", value)}
              placeholder="Describe access to the site..."
              options={accessTypeOptions}
              required
              readOnly={readOnly}
            />
          </div>

          <div className="space-y-2">
            <OptimizedInput
              id="visibilitySplays"
              label="Visibility splays"
              value={formData.visibilitySplays}
              readOnly={readOnly}
              onChange={(value) => handleInputChange("visibilitySplays", value)}
              placeholder="Describe visibility splays..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedCheckbox
              id="narrowBridge"
              label="Narrow bridge"
              checked={formData.narrowBridge}
              onChange={(checked) => handleInputChange("narrowBridge", checked)}
              readOnly={readOnly}
            />
            <OptimizedCheckbox
              id="lowHeightBridge"
              label="Low height bridge"
              checked={formData.lowHeightBridge}
              onChange={(checked) =>
                handleInputChange("lowHeightBridge", checked)
              }
              readOnly={readOnly}
            />
          </div>

          <div className="space-y-2">
            <OptimizedInput
              id="accessRoadWidth"
              label="Access road width"
              value={formData.accessRoadWidth}
              onChange={(value) => handleInputChange("accessRoadWidth", value)}
              readOnly={readOnly}
              placeholder="e.g., 3.5m, 4m, etc."
            />
          </div>

          <ImageSection
            title="Site Access Images"
            filterData={{ site_access: true }}
            uploadFilterData={{ site_access: true }}
            emptyMessage="Add images to document site access conditions"
            buttonText="Add Access Images"
          />
        </CardContent>
      </Card>

      {/* Description */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Leaf className="w-5 h-5 text-amber-600" />
            <CardTitle>Site Description</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <OptimizedCheckboxGroup
            id="description"
            label="Which of the following describe the site?"
            values={formData.description}
            onChange={(values) => handleInputChange("description", values)}
            readOnly={readOnly}
            options={siteConditionOptions}
            required
            direction="column"
          />

          <ImageSection
            title="Site Description Images"
            filterData={{ site_description: true }}
            uploadFilterData={{ site_description: true }}
            emptyMessage="Add images to document site description features"
            buttonText="Add Description Images"
          />
        </CardContent>
      </Card>

      {/* Ground / Rooftop Condition */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <CardTitle>Ground / Rooftop Condition</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <OptimizedCheckboxGroup
            id="groundRooftopCondition"
            label="Are any of the following ground or rooftop hazards present at the site?"
            values={formData.groundRooftopCondition}
            onChange={(values) =>
              handleInputChange("groundRooftopCondition", values)
            }
            readOnly={readOnly}
            options={groundRooftopConditionOptions}
            direction="column"
          />

          <ImageSection
            title="Ground / Rooftop Condition Images"
            filterData={{ ground_rooftop_condition: true }}
            uploadFilterData={{ ground_rooftop_condition: true }}
            emptyMessage="Add images to document ground or rooftop surface conditions"
            buttonText="Add Condition Images"
          />
        </CardContent>
      </Card>

      {/* Additional Observations */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <HardHat className="w-5 h-5 text-purple-600" />
            <CardTitle>Additional Observations</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <OptimizedTextarea
            id="additionalObservations"
            label="General Site Notes"
            value={formData.additionalObservations}
            readOnly={readOnly}
            onChange={(value) =>
              handleInputChange("additionalObservations", value)
            }
            placeholder="Record any additional site observations, environmental factors, or special considerations..."
            rows={4}
          />

          <ImageSection
            title="General Site Images"
            filterData={{ general_site: true }}
            uploadFilterData={{ general_site: true }}
            emptyMessage="Add general site images"
            buttonText="Add General Images"
          />
        </CardContent>
      </Card>

      {/* Site Images */}
      {/* <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              <CardTitle>Site Images</CardTitle>
              <span className="text-red-500">*</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.multiple = true;
                input.accept = "image/*";
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement;
                  handleImageUpload(target.files, { site_images: true });
                };
                input.click();
              }}
              disabled={loading || isUploadingForSection({ site_images: true })}
            >
              {isUploadingForSection({ site_images: true }) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {isUploadingForSection({ site_images: true }) ? "Uploading..." : "Add Images"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isUploadingForSection({ site_images: true }) && (
            <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50">
              <div className="text-center">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
                <p className="text-sm text-slate-600">Uploading site images...</p>
              </div>
            </div>
          )}

          {!isUploadingForSection({ site_images: true }) &&
           files?.filter((file) => file?.relatedTo?.filterData?.site_images).length === 0 ? (
            <div className="flex items-center p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-700">No site images yet</p>
                  <p className="text-xs text-slate-500">Add images to document site conditions and features</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {files
                ?.filter((file) => file?.relatedTo?.filterData?.site_images)
                .map((image) => (
                  imageDescription={imageDescriptions[image.id] ?? image?.fileDescription ?? ""}
                image={image}
                    onView={setSelectedImage}
                    onRemove={handleRemoveImage}
                    onDescriptionUpdate={updateImageDescription}
                    isDeleting={loading ? imageToDelete : false}
                  />
                ))}
            </div>
          )}
        </CardContent>
      </Card> */}

      {!isFormValid && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please fill in all required fields (marked with *) to complete this
            section.
          </AlertDescription>
        </Alert>
      )}

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Delete Image</h3>
            </div>
            <p className="text-slate-600 dark:text-muted-foreground mb-6">
              Are you sure you want to delete this image? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRemoveImage}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveImage}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
                disabled={loading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl}
                alt={selectedImage?.originalName || "Selected"}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage?.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage?.fileSize)} • Date:{" "}
                {new Date(selectedImage?.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Form Status:
            </span>
            <Badge variant={isFormValid ? "default" : "secondary"}>
              {isFormValid ? "Complete" : "Incomplete"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress?.[templateId]?.["site-data"] || 0}%
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
